import { supabase } from '@/integrations/supabase/client';
import { readCachedIpLocation, writeCachedIpLocation } from './locationCache';

export interface UserLocation {
  country: string;
  state_province: string;
  latitude: number | null;
  longitude: number | null;
  auto_location_enabled: boolean;
}

// Get user's saved location from profile
export const getUserSavedLocation = async (userId: string): Promise<UserLocation | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('country, state_province, latitude, longitude, auto_location_enabled')
      .eq('id', userId)
      .single();

    if (error) throw error;

    return data as UserLocation;
  } catch (error) {
    console.error('Error fetching user location:', error);
    return null;
  }
};

// Auto-detect user's location using geolocation API
export const detectUserLocation = async (): Promise<{ region: string; country: string }> => {
  const fallback = () => ({ region: 'climate science', country: 'general' });

  if (!navigator.geolocation) return fallback();
  // Geolocation requires a secure context (https or localhost)
  if (!window.isSecureContext) return fallback();

  const getPosition = (options: PositionOptions) =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });

  try {
    // High-accuracy first (GPS), then fallback to network-based.
    let position: GeolocationPosition;
    try {
      position = await getPosition({
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
    } catch {
      position = await getPosition({
        enableHighAccuracy: false,
        timeout: 20000,
        maximumAge: 0,
      });
    }

    const { latitude, longitude } = position.coords;

    // Reverse geocode (best-effort)
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
      );
      if (response.ok) {
        const data = await response.json();
        const state =
          data?.principalSubdivision || data?.principalSubdivisionCode || '';
        const country = data?.countryName || '';
        const region = state ? `${state}, ${country}` : country || 'climate science';
        return { region, country: country || 'general' };
      }
    } catch (error) {
      console.error('Error getting location name:', error);
    }

    // If reverse geocode fails, return coordinates-derived generic region label
    return { region: 'your area', country: 'general' };
  } catch (error: any) {
    console.error('Error getting user location:', error);

    // Fallback: when POSITION_UNAVAILABLE (code 2), approximate via IP-based geolocation.
    if (typeof error?.code === 'number' && error.code === 2) {
      try {
        const cached = readCachedIpLocation();
        if (cached && (cached.region || cached.country)) {
          const region = cached.region
            ? `${cached.region}, ${cached.country || ''}`.replace(/,\s*$/, '')
            : cached.country || 'climate science';
          return { region, country: cached.country || 'general' };
        }

        const resp = await fetch(
          'https://api.bigdatacloud.net/data/ip-geolocation?localityLanguage=en',
        );
        if (resp.ok) {
          const ipData = await resp.json();
          const state =
            ipData?.location?.principalSubdivision ||
            ipData?.location?.principalSubdivisionCode ||
            '';
          const country = ipData?.country?.name || ipData?.countryName || '';
          const region = state ? `${state}, ${country}` : country || 'climate science';
          if (state || country) {
            writeCachedIpLocation({
              country,
              region: state,
              latitude: null,
              longitude: null,
            });
            return { region, country: country || 'general' };
          }
        }
      } catch (ipErr) {
        console.error('IP geolocation fallback failed:', ipErr);
      }
    }

    return fallback();
  }
};

// Get user location - first try saved location, then auto-detect if enabled
export const getUserLocation = async (userId?: string): Promise<{ region: string; country: string }> => {
  if (userId) {
    const savedLocation = await getUserSavedLocation(userId);
    
    if (savedLocation?.country && savedLocation?.state_province) {
      const region = `${savedLocation.state_province}, ${savedLocation.country}`;
      return { region, country: savedLocation.country };
    }
    
    if (savedLocation?.country) {
      return { region: savedLocation.country, country: savedLocation.country };
    }
    
    // If auto-location is enabled and no saved location, try to detect
    if (savedLocation?.auto_location_enabled !== false) {
      return await detectUserLocation();
    }
  }
  
  // Fallback to auto-detection for non-authenticated users
  return await detectUserLocation();
};
